import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import moment from 'moment';
import { v4 as uuid } from 'uuid';
import { confirmAlert } from 'react-confirm-alert';

//components
import withLocalization from '~/hoc/withLocalization';
import UrgentCallList from './UrgentCallList';

//elements
import Button from '~/components/CustomButton/CustomButton.jsx';
import LoadingSpinner from '~/elements/LoadingSpinner';
import AlertMessage, { createAlert } from '../../../../components/Alert/AlertMessage.jsx';

//utils
import { getWeekNumbersBetweenDates } from '~/utils/commonMethods';

//styles
import './styles.css';

class UrgentCallForm extends Component {
    constructor(props) {
        super(props);
        const { urgentCallEditing , scheduleStore, conflictMessage } = props;
        const date = urgentCallEditing && urgentCallEditing.for_date ? moment(urgentCallEditing.for_date) : moment();
        this.currentYear = (urgentCallEditing && urgentCallEditing.for_date) ? moment(urgentCallEditing.for_date).isoWeekYear() : (scheduleStore.currantViewYear || moment().isoWeekYear());
        this.weeksInYear = getWeekNumbersBetweenDates(moment().isoWeekYear(this.currentYear).startOf('year'), moment().isoWeekYear(this.currentYear).endOf('year'));
        this.selectedWeekRef = React.createRef();
        this.weekPanelBodyRef = React.createRef();
        this.state = {
            selectedWeek: urgentCallEditing?.weekNumber || scheduleStore.currantViewYearWeek || date.isoWeek(),
            weekWiseUrgentCalls: null,
            weekUniqueId: uuid(),
            isLoading: true,
            hasUnsavedChanges: false,
            alertMessages: conflictMessage ? [createAlert(conflictMessage, 'error')] : []
        };
        this.handleUnsavedChanges = this.handleUnsavedChanges.bind(this);
    }

    removeAlert = (messages) => {
        this.setState({ alertMessages: messages });
    }

    componentDidMount() {
        this.setState({ isLoading: true }, () => this.loadData());
    }

    loadData = async () => {
        const { urgentCallsStore, commonStore, t, urgentCallEditing } = this.props
        const { selectedWeek } = this.state;
        try {
            const data = await urgentCallsStore.loadUrgentCallWeeklyCounts(this.currentYear);
            this.setState({
                weekWiseUrgentCalls: data,
                isLoading: false,
                weekUniqueId: uuid(),
                selectedWeek: urgentCallEditing?.weekNumber || selectedWeek
            }, () => {
                this.selectedWeekRef.current?.scrollIntoView({ behavior: 'smooth' });
            })
        } catch (error) {
            console.error(error);
            commonStore.addNotification(t('Something went wrong'), null, 'error');
            this.setState({ isLoading: false })
        }
    }

    handleWeekChange = (weekNumber) => {
        const { t } = this.props;
        const { hasUnsavedChanges } = this.state;

        if (hasUnsavedChanges) {
            confirmAlert({
                title: t('Are you sure?'),
                message: t('Are you sure you want to move without saving?'),
                buttons: [
                    {
                        label: t('Yes'),
                        onClick: () => {
                            this.setState({
                                selectedWeek: weekNumber,
                                weekUniqueId: uuid(),
                                hasUnsavedChanges: false
                            });
                        }
                    },
                    {
                        label: t('No'),
                        onClick: () => { }
                    }
                ]
            });
        } else {
            this.setState({ selectedWeek: weekNumber, weekUniqueId: uuid() });
        }
    }

    handleUnsavedChanges = (hasChanges) => {
        this.setState({ hasUnsavedChanges: hasChanges });
    }

    componentWillUnmount() {
        this.selectedWeekRef = null;
        this.weekPanelBodyRef = null;
    }

    render() {
        const { t, handleCloseUrgentShiftModal, urgentCallEditing } = this.props;
        const { selectedWeek, weekWiseUrgentCalls, isLoading, weekUniqueId, alertMessages } = this.state;
        if (isLoading) {
            return <LoadingSpinner />
        }
        return (
            <div className='urgent-call-management'>
                <div className='week-panel'>
                    <div className="w-100 mb-2 d-flex justify-content-space-between">
                        <Button
                            disabled={selectedWeek === 1}
                            onClick={() => this.handleWeekChange(selectedWeek - 1)} >
                            <i className="fas fa-chevron-left me-2"></i>
                            {t('Prev')}
                        </Button>
                        <Button fill
                            disabled={selectedWeek === this.weeksInYear.length}
                            onClick={() => this.handleWeekChange(selectedWeek + 1)}>
                            {t('Next')}
                            <i className="fas fa-chevron-right ms-2"></i>
                        </Button>
                    </div>
                    <div className='week-panel-header'>
                        <h5 className="fw-bold mb-0">{t('Weeks')} {this.currentYear}</h5>
                    </div>
                    <div className='week-panel-body' ref={this.weekPanelBodyRef}>
                        {this.weeksInYear.map(w => (
                            <div
                                key={w}
                                className={`week-item ${w === selectedWeek ? 'week-item-active' : ''}`}
                                onClick={() => this.handleWeekChange(w)}
                                ref={w > 10 && w === (selectedWeek - 1) ? this.selectedWeekRef : null}
                            >
                                <div className="d-flex justify-content-between align-items-center">
                                    <span className="fw-bold">{t('Week')} {w}</span>
                                    {!!weekWiseUrgentCalls && weekWiseUrgentCalls[w] && (
                                        <span className="badge bg-danger rounded-circle">
                                            {weekWiseUrgentCalls[w]}
                                        </span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className='urgent-call-container'>
                    <AlertMessage messages={alertMessages} onClose={this.removeAlert} maxLimit={2} />
                    <UrgentCallList
                        year={this.currentYear}
                        weekNumber={selectedWeek}
                        key={weekUniqueId}
                        hasUrgentCallData={weekWiseUrgentCalls && weekWiseUrgentCalls[selectedWeek]}
                        reloadData={() => this.loadData()}
                        handleCloseUrgentShiftModal={handleCloseUrgentShiftModal}
                        urgentCallEditing={urgentCallEditing}
                        handleUnsavedChanges={this.handleUnsavedChanges}
                    />
                </div>
            </div>
        );
    }
}

export default inject('urgentCallsStore', 'commonStore', 'scheduleStore')(observer(withLocalization(UrgentCallForm)));